import { api } from '@/lib/api';
import type { Message } from '@/types';

const IMAGE_MIME = new Set(['image/jpeg', 'image/png']);
const DOCUMENT_MIME = new Set(['application/pdf']);
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const DOCUMENT_MAX_BYTES = 16 * 1024 * 1024;

export const CONVERSATION_MEDIA_ACCEPT = 'image/jpeg,image/png,application/pdf';

export type ConversationMediaKind = 'image' | 'document';

export class ConversationMediaError extends Error {
  constructor(
    readonly code: 'unsupported' | 'too_large' | 'upload_failed',
    message: string,
  ) {
    super(message);
    this.name = 'ConversationMediaError';
  }
}

export function classifyConversationMedia(file: File): ConversationMediaKind {
  if (IMAGE_MIME.has(file.type)) {
    if (file.size > IMAGE_MAX_BYTES) {
      throw new ConversationMediaError('too_large', 'image');
    }
    return 'image';
  }
  if (DOCUMENT_MIME.has(file.type)) {
    if (file.size > DOCUMENT_MAX_BYTES) {
      throw new ConversationMediaError('too_large', 'document');
    }
    return 'document';
  }
  throw new ConversationMediaError('unsupported', file.type);
}

export async function sendConversationMedia(
  conversationId: string,
  file: File,
  caption?: string,
): Promise<Message> {
  const type = classifyConversationMedia(file);
  const presign = await api.post<{ uploadUrl: string; fileKey: string }>(
    `/conversations/${conversationId}/media-upload-url`,
    {
      mimeType: file.type,
      fileName: file.name,
      fileSizeBytes: file.size,
    },
  );

  const uploaded = await fetch(presign.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!uploaded.ok) {
    throw new ConversationMediaError('upload_failed', String(uploaded.status));
  }

  return api.post<Message>(`/conversations/${conversationId}/messages`, {
    type,
    fileKey: presign.fileKey,
    fileName: file.name,
    mimeType: file.type,
    ...(caption && caption.trim().length > 0 ? { caption: caption.trim() } : {}),
  });
}
